import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { MessageCircle, Phone, Mail, MapPin } from 'lucide-react';

const ComplaintsPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO title="Complaints Policy" description="How to raise a complaint with Manage Your Loans and our resolution process." path="/complaints-policy" />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#00594C] to-[#003d35] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <MessageCircle className="w-8 h-8 text-[#99DF63]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Complaints & Dispute Resolution Policy</h1>
            <p className="text-lg text-gray-200">
              We're committed to addressing your concerns promptly and fairly
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">

            <p className="text-lg text-gray-600 leading-relaxed mb-10">
              At Manage Your Loans, we pride ourselves on offering exceptional service to our clients. In the event that we have not met your expectations, we genuinely wish to hear from you to address your concerns in a constructive manner.
            </p>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-6">How to Lodge a Complaint</h2>

            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-[#00594C] mb-3">1. Initial Point of Contact</h3>
                <p className="text-gray-600 leading-relaxed">
                  We encourage you to first raise your concerns with the person who initially assisted you. They are best placed to address your immediate concerns.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-[#00594C] mb-3">2. Complaints Team</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  If the initial contact is unable to resolve your complaint, or if you feel more comfortable bypassing this step, please reach out to our dedicated Complaints Team:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-[#00594C] flex-shrink-0" />
                    <a href="tel:+61468007056" className="text-[#00594C] font-semibold hover:underline">0468 007 056</a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-[#00594C] flex-shrink-0" />
                    <a href="mailto:complaints@manageyourloans.com.au" className="text-[#00594C] font-semibold hover:underline">complaints@manageyourloans.com.au</a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-[#00594C] flex-shrink-0" />
                    <span className="text-gray-600">Manage Your Loans Complaints Team, PO Box 1808, Darwin, NT 0801</span>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-[#00594C] mt-12 mb-6">Internal Dispute Resolution Procedure</h2>

            <div className="space-y-6">
              <div className="flex space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#99DF63] rounded-full flex items-center justify-center">
                  <span className="text-[#00594C] font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#00594C] mb-2">Stage 1</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Your MYL representative will strive to address your complaint promptly. If the outcome is not to your satisfaction or if you would like a second review, you can request your complaint to be escalated to Stage 2.
                  </p>
                </div>
              </div>

              <div className="flex space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#99DF63] rounded-full flex items-center justify-center">
                  <span className="text-[#00594C] font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#00594C] mb-2">Stage 2</h3>
                  <p className="text-gray-600 leading-relaxed">
                    A Dispute Resolution Manager will assess your complaint. This individual will, wherever feasible, be distinct from the original respondent of the complaint to ensure impartiality. Our response will be in writing, and it will include reasons for any decisions we have made.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-gray-600 leading-relaxed mt-6">
              If, after our internal process concludes, you are unsatisfied with our response, you are entitled to seek recourse via an External Dispute Resolution service.
            </p>

            <h2 className="text-2xl font-bold text-[#00594C] mt-12 mb-6">External Dispute Resolution Services</h2>

            <div className="space-y-6">
              <div className="bg-[#00594C]/5 rounded-xl p-6 border border-[#00594C]/10">
                <h3 className="text-lg font-bold text-[#00594C] mb-4">Australian Financial Complaints Authority (AFCA)</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-[#00594C] flex-shrink-0" />
                    <span className="text-gray-600">1800 931 678 (free call) or 03 9613 6399</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-[#00594C] flex-shrink-0" />
                    <a href="mailto:info@afca.org.au" className="text-[#00594C] font-semibold hover:underline">info@afca.org.au</a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-[#00594C] flex-shrink-0" />
                    <span className="text-gray-600">GPO Box 3, Melbourne VIC 3001</span>
                  </div>
                  <p className="text-gray-600 text-sm mt-2">
                    Website: <a href="https://www.afca.org.au" target="_blank" rel="noopener noreferrer" className="text-[#00594C] font-semibold hover:underline">afca.org.au</a>
                  </p>
                </div>
              </div>

              <div className="bg-[#00594C]/5 rounded-xl p-6 border border-[#00594C]/10">
                <h3 className="text-lg font-bold text-[#00594C] mb-4">Office of the Australian Information Commissioner (OAIC)</h3>
                <p className="text-sm text-gray-500 mb-4">For Privacy Complaints</p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-[#00594C] flex-shrink-0" />
                    <span className="text-gray-600">1300 363 992</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-[#00594C] flex-shrink-0" />
                    <a href="mailto:enquiries@oaic.gov.au" className="text-[#00594C] font-semibold hover:underline">enquiries@oaic.gov.au</a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-[#00594C] flex-shrink-0" />
                    <span className="text-gray-600">GPO Box 5218, Sydney NSW 2001</span>
                  </div>
                  <p className="text-gray-600 text-sm mt-2">
                    Website: <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="text-[#00594C] font-semibold hover:underline">www.oaic.gov.au</a> (online complaint form available)
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-[#00594C] mt-12 mb-6">Our Commitment to You During the Complaint Process</h2>

            <div className="bg-white rounded-xl border-2 border-gray-100 p-6">
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#99DF63] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-600">We will acknowledge your complaint within <strong>one business day</strong> of receipt.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#99DF63] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-600">You will be kept updated regarding the progress of your complaint at regular intervals, no longer than every <strong>ten (10) calendar days</strong>.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#99DF63] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-600">To thoroughly address your complaint, we may seek additional information from you.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#99DF63] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-600">Should we be unable to resolve your complaint within <strong>5 business days</strong> of receipt, or if you request a written response, or if the complaint pertains to financial hardship, a formal Internal Dispute Resolution response will be provided.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#99DF63] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-600">In situations where a formal response is not mandated, we will confirm our actions to address your complaint in writing.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#99DF63] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-600">We pledge to provide a resolution or a definitive outcome to your complaint within <strong>30 calendar days</strong> from the initial receipt date.</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-600 leading-relaxed mt-8">
              For detailed information on how we manage and protect your personal data, please see our <a href="/privacy-policy" className="text-[#00594C] font-semibold hover:underline">Privacy Policy</a>.
            </p>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ComplaintsPolicy;
